// =============================================================================
// client/src/components/flux/ConnectGmail.jsx | v1.0 | S84 | P9a — W-11
// =============================================================================
// The Gmail connect flow. Drives the eleven-state machine that
// client/src/lib/flux/connect.js holds as data (P9a §0.6, adjudication B-22).
//
// Consumed by the builder's step 1 and — unchanged — by P9b's RecruitEdit card.
// That is why the state machine lives in connect.js and not in here: two
// consumers, one contract, and a component cannot be the source of truth for a
// contract another page also implements.
//
// 🔴 `verified` IS THE ONLY STATE THAT ENABLES LAUNCH OR TEST, and this
// component never decides that — `enablesLaunch(state)` does, and the parent
// asks it. `connected` and `verified` are DIFFERENT FACTS: /oauth/status proves
// a token ROW exists; /gmail-verify proves the token WORKS. v1 called both
// "connected" and gated Launch on a separate boolean, which is how a client
// ships a Launch button over a dead token.
//
// 🔴 ONE SUCCESSFUL VERIFY PER CONNECT EPISODE (ruling 16, P-4 — BENT at S99,
// Jake's ruling). /gmail-verify asks GOOGLE. The poll is the cheap loop —
// /oauth/status, every 5 s, our own database — and a verify that SUCCEEDS still
// fires exactly once. What changed: a verify that FAILS mid-poll re-arms,
// because a dead token row is what every re-auth episode starts with, and
// treating it as the episode's answer meant re-auth could never work (see the
// CONNECTED branch in checkStatus). Ruling 16's purpose — never verify on
// keystrokes — is untouched. verifiedFor below is what enforces all of this;
// do not "simplify" it away.
//
// 🔴 NO postMessage AND NO window.close() (ruling 1 step 5, superseded by
// P4 §10). The connected page tells the kid he can close the window
// (flux-oauth-pages.js:69). P4 §10 left "P9a decides a courtesy window.close()"
// open; it is decided: NONE.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api.js';
import {
  CONNECT_STATES, offersConnectControls, stateForStatusResult, stateForVerifyResult,
  pollDelayMs, pollDeadlineMs,
} from '../../lib/flux/connect.js';

// Hand edits to the address re-run the status check after this long — and only
// if the address actually differs from the connected one. NEVER verify on
// keystrokes: /gmail-verify is a Google round trip (RM :320-332).
const EDIT_DEBOUNCE_MS = 600;

const LABELS = {
  [CONNECT_STATES.IDLE]: '',
  [CONNECT_STATES.CHECKING]: 'Checking Gmail…',
  [CONNECT_STATES.NOT_CONNECTED]: 'Not connected',
  [CONNECT_STATES.POLLING]: 'Waiting for authorization…',
  [CONNECT_STATES.EXPIRED]: 'Not yet authorized',
  [CONNECT_STATES.CONNECTED]: 'Connected — verifying…',
  [CONNECT_STATES.VERIFIED]: '✓ Connected',
  [CONNECT_STATES.REVOKED]: '⚠ Needs re-authorization',
  [CONNECT_STATES.BLOCKED]: '',   // the server's reason IS the label
  [CONNECT_STATES.AMBIGUOUS]: 'More than one Gmail connection on file — an admin has to resolve it',
  [CONNECT_STATES.ERROR]: '',     // the failure IS the label
};

const PILL = {
  [CONNECT_STATES.VERIFIED]: 'bg-green-100 text-green-800',
  [CONNECT_STATES.CONNECTED]: 'bg-blue-100 text-blue-800',
  [CONNECT_STATES.POLLING]: 'bg-blue-100 text-blue-800',
  [CONNECT_STATES.CHECKING]: 'bg-gray-200 text-silver-text',
  [CONNECT_STATES.REVOKED]: 'bg-amber-100 text-amber-800',
  [CONNECT_STATES.EXPIRED]: 'bg-amber-100 text-amber-800',
  [CONNECT_STATES.BLOCKED]: 'bg-red-200 text-red-800',
  [CONNECT_STATES.AMBIGUOUS]: 'bg-red-200 text-red-800',
  [CONNECT_STATES.ERROR]: 'bg-red-200 text-red-800',
  [CONNECT_STATES.NOT_CONNECTED]: 'bg-gray-200 text-silver-text',
};

export default function ConnectGmail({ recruitId, gmail, onState }) {
  const [state, setState] = useState(recruitId ? CONNECT_STATES.CHECKING : CONNECT_STATES.IDLE);
  // 🔴 DEFECT D-1. This was `useState(gmail || '')` alone, and useState reads
  // its argument ONCE AT MOUNT — every later value of the `gmail` prop was
  // ignored. The builder mounts this component from pickRecruit's
  // setSendFrom(r.email), so `mailbox` froze at the recruit's RECORD email;
  // the operator then typed into the PARENT's Sending-from field and mintLink()
  // kept sending the frozen value. A kid's mailbox credential was minted for an
  // address the operator never chose, while the screen showed the one they did.
  //
  // The server anticipated exactly this — flux-oauth.js at login_hint:
  //   "the signed target, NOT recruits.email. recruits.email may be a parent's
  //    or a school address; this is the mailbox actually being connected."
  //
  // ⚠ THE PREFILL WAS NEVER WRONG (Jake: "Record email should be default and in
  // the box already, but EDITABLE"). What was broken is that editing it was
  // COSMETIC. Do not "fix" the prefill; the parent still supplies it.
  const [mailbox, setMailbox] = useState(gmail || '');
  const [detail, setDetail] = useState(null);      // the server's reason / error, verbatim

  // 🔴 D-1's FIX. Track the prop. The parent owns the address the operator
  // edits; this component owns only what the SERVER later reports as connected
  // (:checkStatus). Without this, the two disagree the moment anyone types.
  //
  // Guarded on inequality so it cannot fight checkStatus's own write: when
  // /oauth/status reports a connected mailbox the parent is told through
  // onState/its own field, and re-running this on every render would loop.
  useEffect(() => {
    const next = gmail || '';
    setMailbox((cur) => (cur === next ? cur : next));
  }, [gmail]);
  const [link, setLink] = useState(null);          // { url, expires_at }
  const [elapsed, setElapsed] = useState(0);
  const [copied, setCopied] = useState(false);

  const pollRef = useRef(null);
  const startedRef = useRef(null);
  // 🔴 S98. The wall-clock instant this episode stops at, derived from the
  // SERVER's expires_at rather than a constant — 24 h for an emailed invite,
  // ~15 min for a copied link. See connect.js for why a flat timeout is wrong.
  const deadlineRef = useRef(0);
  // The address already verified in THIS episode. Reset when the recruit
  // changes or a fresh connect link is minted — those start a new episode.
  const verifiedFor = useRef(null);
  const aliveRef = useRef(true);
  const onStateRef = useRef(onState);
  onStateRef.current = onState;
  // 🔴 The address as of RIGHT NOW, not as of the last render. `go` is a
  // useCallback with no deps, so it cannot close over `mailbox` — and the one
  // moment the parent most needs the address is the CONNECTED branch, where
  // setMailbox() has been called but has NOT yet re-rendered. Reading state
  // there would report the address the server just replaced. checkStatus writes
  // this ref explicitly for exactly that reason.
  const mailboxRef = useRef(mailbox);
  mailboxRef.current = mailbox;
  // 🔴 S98. `state` as of RIGHT NOW, for callbacks that resume after an await.
  // Same reason mailboxRef exists: `go` has no deps and cannot close over state,
  // and the resume path below must not restart a poll on a machine that has
  // already reached connected.
  const stateRef = useRef(state);
  stateRef.current = state;
  // The deadline of an episode an address edit interrupted. 0 = nothing to
  // resume. See the hand-edit effect for why it resumes rather than abandons.
  const resumeUntilRef = useRef(0);

  const stopPolling = useCallback(() => {
    // ⚠ clearTimeout, not clearInterval: the loop is a self-rescheduling
    // timeout now, because the cadence CHANGES mid-episode and setInterval
    // cannot do that.
    if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; }
    startedRef.current = null;
  }, []);

  // One place sets state, so onState can never miss a transition.
  //
  // 🔴 onState CARRIES THE ADDRESS AS WELL AS THE STATE (S85, OC's ruling).
  // It used to carry the state alone, which meant the parent could learn that a
  // token EXISTS but never which mailbox it is bound to. §5 1b and §6 W-11 step
  // 1 both say `sendFrom` is set from the connected `gmail_address`; there was
  // no wire to set it with, so the builder kept the recruit's RECORD email, the
  // card showed the token's address, and Save answered 400 identity mismatch
  // with no keystroke involved. CampaignBuilder's own comment claimed this
  // component overwrote the parent's field — it could not, and that comment is
  // corrected in the same commit as this line.
  const go = useCallback((next, info) => {
    if (!aliveRef.current) return;
    stateRef.current = next;
    setState(next);
    if (info !== undefined) setDetail(info);
    onStateRef.current?.(next, mailboxRef.current);
  }, []);

  // ── step 2: verify — once on success, re-armed on a mid-poll failure ──────
  // Returns the computed state so the polling caller can decide whether the
  // episode is over. Non-poll callers behave exactly as before.
  const verifyOnce = useCallback(async (address, { fromPoll = false } = {}) => {
    if (!address) return undefined;
    if (verifiedFor.current === address) return undefined; // already asked Google this episode
    verifiedFor.current = address;
    let next;
    let reason;
    try {
      // ⚠ T-8: /gmail-verify lives under /campaigns, NOT under /oauth.
      // /oauth/status is recruit-keyed; /gmail-verify is mailbox-keyed.
      // 🔴 20 s ON PURPOSE — SHORTER THAN THE 45 s DEFAULT, NOT LONGER, AND
      // DO NOT "FIX" IT UPWARD. This is the call the whole timeout build
      // exists for, and it is the one case where a LONG bound is the wrong
      // answer: the poll below RE-ARMS on a failed verify, so a fast abort
      // simply retries on the next tick, where 45 s freezes a buttonless card
      // for the full 45 s. The caller retries; the bound should be short.
      const result = await api.get(
        `/api/flux/campaigns/gmail-verify?gmail=${encodeURIComponent(address)}`,
        { timeoutMs: 20_000 },
      );
      next = stateForVerifyResult(result);
      reason = result?.reason || null;
    } catch (err) {
      next = CONNECT_STATES.ERROR;
      reason = err.body?.error || err.message;
    }
    // 🔴 S99. A FAILED verify during a poll is "not yet", not an answer. Every
    // re-auth episode BEGINS with a token row that exists and is dead, so a
    // mid-poll REVOKED (or a transient ERROR — connect.js maps `timeout` there
    // on purpose: "it is transient. Recheck") must not be written to the card.
    // The card stays POLLING with no flicker, and clearing verifiedFor is what
    // makes the next tick ask Google again instead of hitting the once-per-
    // episode guard above.
    if (fromPoll && (next === CONNECT_STATES.REVOKED || next === CONNECT_STATES.ERROR)) {
      verifiedFor.current = null;
      return next;
    }
    // The reason is rendered VERBATIM. This component does not translate the
    // server's words into friendlier ones — a reason nobody can search for is
    // a reason nobody can fix.
    go(next, reason);
    return next;
  }, [go]);

  // ── step 1: status ────────────────────────────────────────────────────────
  const checkStatus = useCallback(async ({ fromPoll = false } = {}) => {
    if (!recruitId) { go(CONNECT_STATES.IDLE, null); return; }
    if (!fromPoll) go(CONNECT_STATES.CHECKING, null);
    try {
      const status = await api.get(`/api/flux/oauth/status?recruitId=${encodeURIComponent(recruitId)}`);
      const next = stateForStatusResult(status);

      if (next === CONNECT_STATES.CONNECTED) {
        const address = status?.gmail_address || '';
        // The ref is written BEFORE go() so the parent is told the address the
        // server just reported, not the one it is replacing. setMailbox alone
        // would not be visible until the next render, which is after go().
        if (address) { mailboxRef.current = address; setMailbox(address); }
        if (!fromPoll) {
          stopPolling();
          go(CONNECT_STATES.CONNECTED, null);
          await verifyOnce(address);
          return;
        }
        // 🔴 S99. `connected: true` is NOT the poll's success predicate — by
        // connect.js's own header it proves a token ROW exists, nothing about
        // the token WORKING. A re-authorization STARTS with a row that exists
        // and is dead, so stopping here killed every re-auth episode on its
        // first tick: the card flashed CONNECTED, verify answered
        // token_rejected, and REVOKED landed on a poll nothing would ever
        // restart (Jake's 09-01 walk, deterministic). During a poll, only a
        // WORKING token — or a verdict re-auth cannot fix — ends the episode.
        // Do not "simplify" the stopPolling() back in.
        const verdict = await verifyOnce(address, { fromPoll: true });
        if (verdict === CONNECT_STATES.VERIFIED || verdict === CONNECT_STATES.BLOCKED) {
          stopPolling();
          return;
        }
        return; // still waiting: the loop lives, and the next tick asks again
      }
      // ambiguous renders a message and NO mailbox identity — /oauth/status
      // returns no list, so a card that names them is inventing them.
      if (next === CONNECT_STATES.AMBIGUOUS) { stopPolling(); go(next, null); return; }
      // not_connected while polling is the normal waiting case: stay polling.
      if (fromPoll) return;
      go(next, null);
    } catch (err) {
      if (err.status === 403) { stopPolling(); go(CONNECT_STATES.BLOCKED, 'This recruit is not in your scope.'); return; }
      if (fromPoll) return;                       // one failed poll is not a state change
      go(CONNECT_STATES.ERROR, err.body?.error || err.message);
    }
  }, [recruitId, go, stopPolling, verifyOnce]);

  // ── mount / recruit change ────────────────────────────────────────────────
  useEffect(() => {
    aliveRef.current = true;
    verifiedFor.current = null;                   // a new recruit is a new episode
    setLink(null);
    setElapsed(0);
    checkStatus();
    return () => { aliveRef.current = false; stopPolling(); };
  }, [recruitId, checkStatus, stopPolling]);

  // ── the poll ──────────────────────────────────────────────────────────────
  // 🔴 S98. FAST THEN SLOW, AND IT STOPS AT THE SERVER'S DEADLINE.
  // Every 5 s for the first 10 minutes, then every 5 minutes until
  // `expiresAt` — Jake's ruling. A self-rescheduling setTimeout rather than a
  // setInterval, because an interval's period is fixed at creation and this
  // one changes when the fast phase ends.
  // ⚠ `expiresAt` is the caller's mint response. Copy link mints ~15 minutes,
  // Connect Gmail mints 24 hours, and BOTH stop correctly out of this one line.
  // `until` is an optional absolute ms deadline used only by the resume path;
  // normal callers pass the server's expires_at string.
  const beginPolling = useCallback((expiresAt, until) => {
    stopPolling();
    const startedAt = Date.now();
    startedRef.current = startedAt;
    deadlineRef.current = until || pollDeadlineMs(expiresAt, startedAt);
    setElapsed(0);
    go(CONNECT_STATES.POLLING, null);
    const tick = () => {
      if (!aliveRef.current) { stopPolling(); return; }
      const now = Date.now();
      const ms = now - (startedRef.current || now);
      setElapsed(Math.floor(ms / 1000));
      // Deadline checked BEFORE the request, as it always was: an episode past
      // its expiry must not spend one more call finding that out.
      if (now >= deadlineRef.current) { stopPolling(); go(CONNECT_STATES.EXPIRED, null); return; }
      checkStatus({ fromPoll: true });
      pollRef.current = setTimeout(tick, pollDelayMs(ms));
    };
    pollRef.current = setTimeout(tick, pollDelayMs(0));
  }, [checkStatus, go, stopPolling]);

  // ── hand edits to the address ─────────────────────────────────────────────
  // Re-run STEP 1 only, after a debounce, and only when the address differs
  // from the one already verified. Never verify on keystrokes.
  useEffect(() => {
    if (!recruitId) return undefined;
    if (!mailbox || mailbox === verifiedFor.current) return undefined;
    // 🔴 DEFECT D-2. The minted URL carries the address SIGNED INTO ITS STATE,
    // so a link minted for address A is a link for A forever. `onCopy()` reads
    // `link?.url || await mintLink()` — it reuses a cached link rather than
    // re-minting — and setLink(null) previously fired ONLY on mount/recruit
    // change. Cancel did not clear it and neither did retyping the address.
    //
    // So after D-1: type A -> Copy -> Cancel -> type B -> Copy would have
    // handed out A's link, and the kid would authorize the WRONG MAILBOX while
    // the operator watched a screen that said B. D-1 alone would have converted
    // a consistent bug into an intermittent one.
    //
    // ⚠ S98: THIS NOW GUARDS `onCopy` ALONE — `onConnect` no longer reads the
    // cached link at all; it posts to /oauth/send-invite and the server mints
    // per call. The defect is unchanged for the copy path, which is why this
    // block stays exactly as it is. Only the name in the sentence moved.
    setLink(null);
    // 🔴 DEFECT E's FIX (ledger L-9, P9a §6 W-11 part 3). The guard that used to
    // stand ABOVE this block — `if (state === POLLING) return undefined;` — is
    // gone. It returned before setLink(null) could run, so during POLLING an
    // address edit did nothing at all: no link drop, no status re-check, no
    // state reset. The whole hand-edit contract was off during the exact window
    // an operator is most likely to notice a typo, and §6 W-11 states that
    // contract with NO exception for that state.
    //
    // ⚠ THE ORIGINAL RATIONALE WAS SOUND AND IS PRESERVED: an edit must not
    // fight an in-flight poll. It does not — it ENDS it. Changing the address
    // abandons the authorization attempt for the OLD address, so the honest
    // move is to stop that poll rather than let it keep running against a
    // mailbox no longer on screen. Ending an episode is not racing it.
    //
    // Immediate, not debounced, and deliberately so: the debounce exists to
    // keep keystrokes off /oauth/status — it is about the NETWORK call. Killing
    // a dead poll and dropping the badge to `checking` costs no request, and
    // leaving the card reading "Waiting for authorization… (47s)" for an
    // address the operator just replaced is the lie this defect is made of.
    if (state === CONNECT_STATES.POLLING) {
      // 🔴 S98 DEFECT — THIS ENDED THE EPISODE AND NOTHING EVER RESTARTED IT.
      // The debounced call below is `checkStatus`, a ONE-SHOT. So after an
      // address edit during POLLING the card asked once, showed `not_connected`,
      // and then watched nothing forever. The kid could authorize a minute later
      // and the operator would sit in front of a card that never moved — while a
      // RELOAD showed connected, which is exactly how this surfaced (S98, Jake:
      // "when you reload, it shows it's connected. The polling isn't working").
      //
      // ⚠ ENDING THE EPISODE IS STILL RIGHT and defect E's reasoning is intact:
      // the card must not keep counting "Waiting for authorization… (47s)" for an
      // address the operator just replaced. What was wrong is that it never came
      // back.
      //
      // 🔴 AND THE WATCH IS STILL VALID, WHICH IS WHY IT CAN RESUME AT ALL:
      // /oauth/status is RECRUIT-keyed, not mailbox-keyed (T-8). It does not care
      // which address the episode was started for, so an emailed invite — good for
      // 24 h — stays detectable. Abandoning the poll threw away a working watch.
      resumeUntilRef.current = deadlineRef.current;
      stopPolling();
      go(CONNECT_STATES.CHECKING, null);
    }
    const t = setTimeout(() => {
      verifiedFor.current = null;
      const until = resumeUntilRef.current;
      resumeUntilRef.current = 0;
      Promise.resolve(checkStatus()).then(() => {
        // Resume ONLY if the re-check left us still waiting. If it reported
        // connected, ambiguous, blocked or an error, that IS the answer and a
        // poll would talk over it.
        if (!aliveRef.current) return;
        if (!until || until <= Date.now()) return;
        const s = stateRef.current;
        if (s !== CONNECT_STATES.CHECKING && s !== CONNECT_STATES.NOT_CONNECTED) return;
        beginPolling(undefined, until);
      });
    }, EDIT_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // `state` is deliberately not a dependency beyond the polling guard above:
    // re-running this on every transition would re-check on our own updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mailbox, recruitId]);

  // ── mint a connect link ───────────────────────────────────────────────────
  async function mintLink() {
    try {
      verifiedFor.current = null;                 // a fresh link is a fresh episode
      const body = await api.post('/api/flux/oauth/google/url', {
        recruit_id: recruitId,
        gmail_address: mailbox,
      });
      setLink({ url: body?.url, expires_at: body?.expires_at });
      beginPolling(body?.expires_at);
      return body?.url || null;
    } catch (err) {
      // The named 503s are shown VERBATIM — they say which piece of Google
      // config is missing, and paraphrasing them costs the next debugger.
      go(CONNECT_STATES.ERROR, err.body?.error || err.message);
      return null;
    }
  }

  // ── Connect Gmail — EMAILS THE RECRUIT. It does not open Google here. ─────
  // 🔴 S98. This used to mint a link and `window.open` it, which put Google's
  // consent screen in front of the OPERATOR. The operator cannot consent: it is
  // the kid's mailbox and the kid has to sign in and approve it themselves.
  //
  // Jake, S98: "Connect Gmail should send the email to connect the Gmail.
  // Having it open the page on the team member's PC/phone is pointless. We
  // don't sign them in and approve it, they have to do that. Keep the button,
  // fix the workflow."
  //
  // ⚠ THE SERVER SIDE WAS ALREADY BUILT AND THIS CARD SIMPLY NEVER GOT THE WIRE.
  // OutreachTab's sendInvite has called /oauth/send-invite since P9b — minting a
  // 24-hour link and mailing it to the kid — while this component, on the same
  // job, kept opening a tab. One route, two consumers, and only one of them was
  // connected to it.
  //
  // ⚠ `Copy link` is UNCHANGED and still mints through mintLink(). That path is
  // hand-delivery — read the link down the phone, paste it into a text — and it
  // is the reason mintLink() stays.
  async function onConnect() {
    try {
      verifiedFor.current = null;               // a fresh invite is a fresh episode
      const body = await api.post('/api/flux/oauth/send-invite', {
        recruit_id: recruitId,
        gmail_address: mailbox,
      });
      // ⚠ url is null ON PURPOSE: nothing was minted for THIS card to hand out,
      // the server mailed its own link. Keeping the object rather than null
      // preserves the mint path's shape, and leaves `Copy link` to mint a real
      // URL of its own (`link?.url || await mintLink()` falls through on null).
      setLink({ url: null, expires_at: body?.expires_at });
      beginPolling(body?.expires_at);
      // 🔴 `body.sent_to` — the address the SERVER says it mailed, never the
      // local `mailbox`. They can differ, and the one that matters is the one
      // that was actually used. Written after beginPolling because that calls
      // go(POLLING, null) and would clear it.
      setDetail(`Invite sent to ${body?.sent_to}. Waiting for them to authorize…`);
    } catch (err) {
      // The server's words verbatim: a 429 carries the cooldown wait and a 409
      // names an already-bound mailbox, both written to be shown as-is.
      go(CONNECT_STATES.ERROR, err.body?.error || err.message);
    }
  }

  async function onCopy() {
    const url = link?.url || await mintLink();
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be refused by permissions policy. Say so rather than
      // silently doing nothing — the operator needs the link either way.
      setDetail('Could not copy automatically — the link is open in the new tab instead.');
    }
  }

  function onCancel() { stopPolling(); checkStatus(); }

  const showControls = offersConnectControls(state);
  const label = LABELS[state];

  if (state === CONNECT_STATES.IDLE) return null;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`text-[0.6rem] font-bold uppercase tracking-[0.2em] px-2 py-0.5 rounded ${PILL[state] || PILL[CONNECT_STATES.NOT_CONNECTED]}`}>
          {state.replace('_', ' ')}
        </span>
        <span className="text-sm text-hunter font-semibold">
          {label}
          {state === CONNECT_STATES.POLLING && elapsed > 0 ? ` (${elapsed}s)` : ''}
        </span>
        {state === CONNECT_STATES.VERIFIED && mailbox ? (
          <span className="text-sm text-silver-mute">{mailbox}</span>
        ) : null}
      </div>

      {/* The server's words, verbatim. blocked and error have no label of their
          own precisely so the reason is the whole message. */}
      {detail ? <p className="mt-2 text-sm text-silver-mute">{detail}</p> : null}

      {/* ⚠ NO CONTROLS IN `blocked` OR `ambiguous`. A re-auth cannot fix either
          one — one is a wrong-mailbox pairing or a server misconfiguration, the
          other is admin-owned — and offering a button that cannot work mails a
          confused kid for nothing (§0.6). */}
      {showControls ? (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={onConnect}
            className="text-sm font-semibold px-3 py-1.5 rounded bg-mid-green text-white hover:opacity-90"
          >
            Connect Gmail
          </button>
          <button
            type="button"
            onClick={onCopy}
            className="text-sm font-semibold px-3 py-1.5 rounded border border-gray-300 text-hunter hover:border-mid-green"
          >
            {copied ? 'Copied' : 'Copy link'}
          </button>
          {state === CONNECT_STATES.POLLING ? (
            <button
              type="button"
              onClick={onCancel}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-silver-mute hover:border-gray-400"
            >
              Cancel
            </button>
          ) : null}
          {(state === CONNECT_STATES.EXPIRED || state === CONNECT_STATES.ERROR) ? (
            <button
              type="button"
              onClick={() => { verifiedFor.current = null; checkStatus(); }}
              className="text-sm px-3 py-1.5 rounded border border-gray-300 text-hunter hover:border-mid-green"
            >
              Recheck
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
